import { divide, PositionWithExtraInfo } from '@orbis-systems/orbis-api-middleware';

/** Percentage base for gain calculations */
const PERCENTAGE_BASE = 100;

/**
 * Calculates the gain percentage between current value and cost value
 *
 * @param value - Current market value of the position
 * @param costValue - Original cost basis of the position
 * @returns The gain percentage, or 0 if costValue is 0 to avoid division by zero
 *
 * @example
 * // Returns 20 for a 20% gain
 * calculateGainPercent(120, 100) // 20
 *
 * // Returns -10 for a 10% loss
 * calculateGainPercent(90, 100) // -10
 */
export function calculateGainPercent(value: number, costValue: number): number {
    return costValue !== 0 ? (value * PERCENTAGE_BASE) / costValue - PERCENTAGE_BASE : 0;
}

/**
 * Calculates the dividend amount per share
 *
 * @param dividend - Total dividend payment received
 * @param quantity - Number of shares held
 * @returns Dividend amount per share
 *
 * @example
 * // Returns 2.5 dividend per share
 * calculateDividendAmount(250, 100) // 2.5
 */
export function calculateDividendAmount(dividend: number, quantity: number): number {
    return divide(dividend, quantity);
}

/**
 * Combines multiple positions of the same security into unified positions
 *
 * This function aggregates positions that share the same symbol, type, and security type.
 * It combines quantities, values, and costs while recalculating derived metrics like
 * gain percentage and dividend amounts.
 *
 * @param positions - Array of positions to combine
 * @returns Array of combined positions with aggregated values
 *
 * @example
 * const positions = [
 *   { symbol: 'AAPL', type: 'stock', securityType: 'Equity', quantity: 100, value: 10000, costValue: 9000 },
 *   { symbol: 'AAPL', type: 'stock', securityType: 'Equity', quantity: 50, value: 5000, costValue: 4500 }
 * ];
 * const combined = combinePositions(positions);
 * // Returns single position with quantity: 150, value: 15000, costValue: 13500
 */
export function combinePositions(positions: PositionWithExtraInfo[]): PositionWithExtraInfo[] {
    const groupedPositions: Record<string, PositionWithExtraInfo> = {};

    for (const position of positions) {
        // Extract key components for grouping
        const { symbol, type, securityType } = position;

        // Create unique key for position grouping
        const positionKey = `${symbol}_${type}_${securityType}`;

        if (!groupedPositions[positionKey]) {
            // First occurrence: initialize with calculated metrics
            const initialPosition = { ...position };
            initialPosition.gainPercent = calculateGainPercent(position.value, position.costValue);
            initialPosition.dividendAmount = calculateDividendAmount(position.dividendPayment, position.quantity);
            groupedPositions[positionKey] = initialPosition;
        } else {
            // Subsequent occurrences: aggregate values
            const existingPosition = groupedPositions[positionKey];

            // Aggregate quantity and monetary values
            existingPosition.quantity += position.quantity;
            existingPosition.costValue += position.costValue;
            existingPosition.value += position.value;
            existingPosition.daysValue += position.daysValue;
            existingPosition.dividendPayment += position.dividendPayment;

            // Recalculate derived metrics based on aggregated values
            existingPosition.gain = existingPosition.value - existingPosition.costValue;
            existingPosition.gainPercent = calculateGainPercent(existingPosition.value, existingPosition.costValue);
            existingPosition.dividendAmount = calculateDividendAmount(
                existingPosition.dividendPayment,
                existingPosition.quantity,
            );

            // Use latest dividend yield
            existingPosition.dividendYield = position.dividendYield;
        }
    }

    return Object.values(groupedPositions);
}
